import { apiUrl } from '@ai-studio/shared';

import { getCsrfToken } from '../lib/api.js';

/**
 * File access for the engine (`host.readFile`).
 *
 * Documents open in the editor are served from an in-memory overlay, so the
 * engine always sees the latest edit — even before the debounced save has hit
 * the disk. Everything else is read as raw bytes from the project.
 */

const overlay = new Map<string, string>();
const encoder = new TextEncoder();

export function setOverlay(path: string, text: string): void {
  overlay.set(normalise(path), text);
}

export function clearOverlay(path?: string): void {
  if (path === undefined) overlay.clear();
  else overlay.delete(normalise(path));
}

function normalise(path: string): string {
  return path.replace(/\\/g, '/').replace(/^\.?\/+/, '');
}

export async function readProjectFile(projectId: number, path: string): Promise<Uint8Array> {
  const key = normalise(path);
  const buffered = overlay.get(key);
  if (buffered !== undefined) return encoder.encode(buffered);
  const cached = memoryCache.get(`${projectId}:${key}`);
  if (cached) return cached.slice();

  const response = await fetch(
    apiUrl(`/projects/${projectId}/raw?path=${encodeURIComponent(key)}`),
    { credentials: 'same-origin' },
  );
  if (!response.ok) {
    throw new Error(`${key}: ${response.status === 404 ? 'not found' : `HTTP ${response.status}`}`);
  }
  return new Uint8Array(await response.arrayBuffer());
}

/**
 * Stores an engine cache file (`.prism/cache/...`, e.g. a built procedural
 * model) in the project, so the next engine load reads it instead of
 * rebuilding. Fire and forget: a failed write only means a rebuild later.
 */
export function writeProjectCache(projectId: number, path: string, bytes: Uint8Array): void {
  // Other engine views of this editor (Scene, Play, previews) reuse it at once.
  memoryCache.set(`${projectId}:${normalise(path)}`, bytes.slice());
  const token = getCsrfToken();
  void fetch(apiUrl(`/projects/${projectId}/cache?path=${encodeURIComponent(normalise(path))}`), {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/octet-stream', ...(token ? { 'x-ai-studio-csrf': token } : {}) },
    body: bytes.slice().buffer as ArrayBuffer,
  }).catch(() => undefined);
}

/** Engine cache files built in this editor session, shared by every engine view. */
const memoryCache = new Map<string, Uint8Array>();

/** Synchronous lookup for the engine (`readCache` host function). */
export function readProjectCache(projectId: number, path: string): Uint8Array | undefined {
  return memoryCache.get(`${projectId}:${normalise(path)}`);
}
